import { useCallback, useEffect, useRef, useState } from 'react';
import './HeroStories.css';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
const FLAG = import.meta.env?.VITE_HERO_STORIES_ENABLED ?? 'true';

const AUTO_INTERVAL = 8000;
const SWIPE_THRESHOLD = 30;

interface Story {
  id?: string;
  title?: string;
  summary?: string;
  image_url?: string;
  link_url?: string;
}

interface HeroStoriesProps {
  disabled?: boolean;
  fallback?: React.ReactNode;
}

export default function HeroStories({ disabled = false, fallback = null }: HeroStoriesProps) {
  const [stories, setStories] = useState<Story[]>([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(() => new Set());
  const touch = useRef({ x: 0, y: 0 });

  const enabled = !disabled && String(FLAG).toLowerCase() !== 'false';

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    fetch(`${CORE_API}/api/v1/public/stories`)
      .then(response => (response.ok ? response.json() : { data: [] }))
      .then(json => {
        const data = json?.data ?? json;
        if (!cancelled) {
          setStories(Array.isArray(data) ? data : []);
          setLoadedIds(new Set());
          setActive(0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStories([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || paused || stories.length < 2) return undefined;
    const interval = setInterval(() => {
      setActive(index => (index + 1) % stories.length);
    }, AUTO_INTERVAL);
    return () => clearInterval(interval);
  }, [enabled, paused, stories.length]);

  const markLoaded = useCallback((id: string) => {
    setLoadedIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const go = useCallback(
    (direction: 'next' | 'prev') => {
      setActive(index => {
        const total = stories.length;
        if (!total) return index;
        const delta = direction === 'next' ? 1 : -1;
        return (index + delta + total) % total;
      });
    },
    [stories.length]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled || !stories.length) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPaused(true);
        go('next');
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPaused(true);
        go('prev');
      }
      if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Space') {
        event.preventDefault();
        setPaused(prev => !prev);
      }
    },
    [enabled, go, stories.length]
  );

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touchPoint = event.touches?.[0];
    if (!touchPoint) return;
    touch.current = { x: touchPoint.clientX, y: touchPoint.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      const touchPoint = event.changedTouches?.[0];
      if (!touchPoint) return;
      const dx = touchPoint.clientX - touch.current.x;
      if (Math.abs(dx) > SWIPE_THRESHOLD) {
        setPaused(true);
        go(dx < 0 ? 'next' : 'prev');
      }
    },
    [go]
  );

  if (!enabled || stories.length === 0) {
    return fallback;
  }

  return (
    <section
      className="hero-stories"
      aria-roledescription="carousel"
      aria-label="Featured stories"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="hero-stories-track">
        {stories.map((story, index) => {
          const storyKey = story?.id ?? `story-${index}`;
          const loaded = loadedIds.has(storyKey);
          return (
            <article
              key={storyKey}
              className={`hero-slide ${index === active ? 'is-active' : ''}`}
              aria-hidden={index === active ? 'false' : 'true'}
            >
              <img
                src={story.image_url}
                alt={story.title}
                className={`hero-img ${loaded ? 'is-loaded' : ''}`}
                loading="lazy"
                decoding="async"
                onLoad={() => markLoaded(storyKey)}
              />
              <div className="hero-overlay">
                <h2 className="hero-title">{story.title}</h2>
                {story.summary ? <p className="hero-summary">{story.summary}</p> : null}
                {story.link_url ? (
                  <a className="hero-cta" href={story.link_url}>
                    Read more
                  </a>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      <div className="hero-controls">
        <button
          type="button"
          className="hero-btn"
          aria-label="Previous story"
          onClick={() => {
            setPaused(true);
            go('prev');
          }}
        >
          ‹
        </button>

        <div className="hero-dots" role="tablist" aria-label="Story slides">
          {stories.map((_, index) => (
            <button
              key={index}
              role="tab"
              aria-selected={index === active}
              className={`hero-dot ${index === active ? 'is-active' : ''}`}
              onClick={() => {
                setPaused(true);
                setActive(index);
              }}
            />
          ))}
        </div>

        <button
          type="button"
          className="hero-btn"
          aria-label="Next story"
          onClick={() => {
            setPaused(true);
            go('next');
          }}
        >
          ›
        </button>

        <button
          type="button"
          className="hero-toggle"
          aria-pressed={paused ? 'true' : 'false'}
          onClick={() => setPaused(prev => !prev)}
        >
          {paused ? 'Play' : 'Pause'}
        </button>
      </div>
    </section>
  );
}
